import React, { useRef, useImperativeHandle } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Props } from './Button.d';
import * as styles from './Button.module.scss';
import { Icon, IconList, Loading } from 'components/core';
import { convertUrlToPath, useReactRouterLink } from 'components/core/router';

const Button = (
  {
    component,
    type = 'neutral',
    htmlType,
    variant = 'solid',
    size,
    iconOnly = false,
    isRounded = false,
    onClick,
    to,
    external = false,
    className,
    isNaked,
    isLoading = false,
    children,
    disabled,
    ...props
  }: Props,
  ref: React.ForwardedRef<any>
) => {
  const shouldUseRouterLink = useReactRouterLink(to);
  const path = convertUrlToPath(to);

  // Render link as button when disabled to support disabled behavior
  let componentType = disabled ? 'button' : component;
  if (!componentType)
    componentType = to ? (shouldUseRouterLink ? RouterLink : 'a') : 'button';

  const classes = [styles.Button];
  if (!isNaked) classes.push(styles[type], styles[variant]);
  if (size) classes.push(styles[size]);
  if (iconOnly) classes.push(styles['iconOnly']);
  if (isRounded) classes.push(styles.rounded);
  if (isLoading) classes.push(styles.loading);
  if (className) classes.push(className);

  const forwardedRef = useRef<any>(null);
  useImperativeHandle(ref, () => forwardedRef.current);

  return React.createElement<any>(
    componentType,
    {
      ref: forwardedRef,
      to: path,
      onClick,
      href: path,
      target: external ? '_blank' : undefined,
      rel: external ? 'noopener noreferrer' : undefined,
      type: htmlType,
      disabled: disabled || isLoading,
      'aria-busy': isLoading ? 'true' : undefined,
      className: classes.join(' ') || '',
      ...props,
    },
    isLoading ? <span>{children}</span> : children, // Wrapping children in a span triggers CSS rule which sets visibility: hidden
    external ? (
      <Icon
        icon={IconList.light.faArrowUpRightFromSquare}
        aria-label="- opens in new window"
        aria-hidden={false}
      />
    ) : (
      ''
    ),
    <div className={styles.Button_loading} aria-live="polite">
      {isLoading && <Loading />}
    </div>
  );
};

const forwardedButton = React.forwardRef<any, Props>(Button) as React.FC<Props>;
export { forwardedButton as Button };
